import { useState } from 'react';
import { useVsjFlow } from './vsj-flow';
import type { MockProperty } from './vsj-mocks';
import { VsjStepHeader } from './VsjStepHeader';
import { IconHouse, IconBuilding, IconLand } from './vsj-icons';
import {
  createValuationSession,
  updateValuationSession,
  type ValuationPropertyType,
  type ValuationCondition,
  type ValuationAgeBucket,
} from './vsj-valuation-api';

type PropertyType = MockProperty['type'];

const TYPES: { value: PropertyType; label: string; Icon: (p: { size?: number }) => JSX.Element }[] = [
  { value: 'casa',         label: 'Casa',         Icon: IconHouse },
  { value: 'departamento', label: 'Departamento', Icon: IconBuilding },
  { value: 'terreno',      label: 'Terreno',      Icon: IconLand },
];

const BACKEND_TYPE: Record<PropertyType, ValuationPropertyType> = {
  casa:         'house',
  departamento: 'apartment',
  terreno:      'land',
};

const CONDITION_OPTIONS: { value: ValuationCondition; label: string }[] = [
  { value: 'to_renovate', label: 'A refaccionar' },
  { value: 'good',        label: 'Bueno' },
  { value: 'very_good',   label: 'Muy bueno' },
  { value: 'brand_new',   label: 'A estrenar' },
];

const AGE_OPTIONS: { value: ValuationAgeBucket; label: string }[] = [
  { value: 'brand_new', label: 'A estrenar' },
  { value: 'lt_10',     label: 'Menos de 10 años' },
  { value: '10_to_30',  label: '10 a 30 años' },
  { value: 'gt_30',     label: 'Más de 30 años' },
];

// ── Stepper ───────────────────────────────────────────────────────────────────

function Stepper({
  label,
  value,
  onChange,
  min = 0,
  max = 20,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
}) {
  const count = value ?? 0;
  return (
    <div className="vsj-stepper">
      <span className="vsj-stepper-label">{label}</span>
      <div className="vsj-stepper-controls">
        <button
          className="vsj-stepper-btn"
          onClick={() => onChange(count <= min ? null : count - 1)}
          aria-label={`Reducir ${label}`}
        >
          −
        </button>
        <span className="vsj-stepper-count">{count === 0 && value === null ? '—' : count}</span>
        <button
          className="vsj-stepper-btn"
          onClick={() => onChange(Math.min(count + 1, max))}
          aria-label={`Aumentar ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function VsjPropertyForm(): JSX.Element {
  const { property, setProperty, goTo, locateResult, place, setSessionToken, sessionToken } =
    useVsjFlow();

  const [internalStep, setInternalStep] = useState<1 | 2>(1);

  // ── Step 1 state ────────────────────────────────────────────────────────────
  const [localType,    setLocalType]    = useState<PropertyType>(property.type);
  const [localSurface, setLocalSurface] = useState<string>(
    property.surfaceM2 != null ? String(property.surfaceM2) : '',
  );

  // ── Step 2 state ────────────────────────────────────────────────────────────
  const [rooms,     setRooms]     = useState<number | null>(null);
  const [bedrooms,  setBedrooms]  = useState<number | null>(null);
  const [bathrooms, setBathrooms] = useState<number | null>(null);
  const [condition, setCondition] = useState<ValuationCondition | null>(null);
  const [ageBucket, setAgeBucket] = useState<ValuationAgeBucket | null>(null);
  const [garage,    setGarage]    = useState<boolean>(false);
  const [patio,     setPatio]     = useState<boolean>(false);
  const [pool,      setPool]      = useState<boolean>(false);

  const [submitting, setSubmitting] = useState(false);

  const surfaceNum = localSurface !== '' ? parseInt(localSurface, 10) : null;
  const isStep1Valid = surfaceNum !== null && surfaceNum > 0;

  // ── Helpers ─────────────────────────────────────────────────────────────────

  async function callCreateSession(): Promise<string | null> {
    if (!locateResult) return null;
    const areaLandM2  = localType === 'terreno' ? surfaceNum : null;
    const areaBuiltM2 = localType !== 'terreno' ? surfaceNum : null;
    try {
      const res = await createValuationSession({
        placeToken:   locateResult.placeToken,
        lng:          place.lng,
        lat:          place.lat,
        propertyType: BACKEND_TYPE[localType],
        areaLandM2,
        areaBuiltM2,
        streetLabel:  locateResult.streetName ?? locateResult.referenceLabel ?? null,
      });
      setSessionToken(res.sessionToken);
      return res.sessionToken;
    } catch {
      return null;
    }
  }

  // ── Step 1 submit ───────────────────────────────────────────────────────────

  async function handleVerRef() {
    if (!isStep1Valid || submitting) return;
    setSubmitting(true);
    await callCreateSession();
    setProperty({ type: localType, surfaceM2: surfaceNum });
    setSubmitting(false);
    goTo('result');
  }

  async function handleAfinar() {
    if (!isStep1Valid || submitting) return;
    setSubmitting(true);
    await callCreateSession();
    setProperty({ type: localType, surfaceM2: surfaceNum });
    setSubmitting(false);
    setInternalStep(2);
  }

  // ── Step 2 submit ───────────────────────────────────────────────────────────

  async function handleStep2Submit() {
    if (submitting) return;
    const token = sessionToken;
    if (token) {
      setSubmitting(true);
      try {
        await updateValuationSession(token, {
          rooms,
          bedrooms,
          bathrooms,
          condition,
          ageYearsBucket: ageBucket,
          garage,
          patio,
          pool,
        });
      } catch {
        // best-effort — proceed to result even if update fails
      } finally {
        setSubmitting(false);
      }
    }
    goTo('result');
  }

  // ── Step 2 render ───────────────────────────────────────────────────────────

  if (internalStep === 2) {
    return (
      <div className="vsj-step">
        <VsjStepHeader step={3} onBack={() => setInternalStep(1)} />

        <div className="vsj-property-form">
          <div>
            <h2 className="vsj-form-title">Más detalles</h2>
            <p className="vsj-form-intro vsj-form-intro--muted">
              Cuantos más datos, más ajustada la referencia.{' '}
              Podés saltear lo que no sabés.
            </p>
          </div>

          <div className="vsj-form-group">
            <label className="vsj-form-label">Distribución</label>
            <div className="vsj-steppers-group">
              <Stepper label="Ambientes"   value={rooms}     onChange={setRooms}     max={20} />
              <Stepper label="Dormitorios" value={bedrooms}  onChange={setBedrooms}  max={20} />
              <Stepper label="Baños"       value={bathrooms} onChange={setBathrooms} max={10} />
            </div>
          </div>

          <div className="vsj-form-group">
            <label className="vsj-form-label">Estado general</label>
            <div className="vsj-chip-group">
              {CONDITION_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  className={'vsj-chip' + (condition === value ? ' vsj-chip--selected' : '')}
                  onClick={() => setCondition(condition === value ? null : value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="vsj-form-group">
            <label className="vsj-form-label">Antigüedad aproximada</label>
            <div className="vsj-chip-group">
              {AGE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  className={'vsj-chip' + (ageBucket === value ? ' vsj-chip--selected' : '')}
                  onClick={() => setAgeBucket(ageBucket === value ? null : value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="vsj-form-group">
            <label className="vsj-form-label">Amenidades</label>
            <div className="vsj-amenity-toggles">
              {(
                [
                  { key: 'garage', label: 'Cochera', value: garage, set: setGarage },
                  { key: 'patio',  label: 'Patio',   value: patio,  set: setPatio  },
                  { key: 'pool',   label: 'Pileta',  value: pool,   set: setPool   },
                ] as const
              ).map(({ key, label, value, set }) => (
                <button
                  key={key}
                  className={'vsj-amenity-toggle' + (value ? ' vsj-amenity-toggle--on' : '')}
                  onClick={() => set(!value)}
                  aria-pressed={value}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="vsj-property-form-footer">
            <button
              className="vsj-btn-primary"
              onClick={handleStep2Submit}
              disabled={submitting}
              aria-disabled={submitting}
            >
              {submitting ? 'Guardando…' : 'Ver la referencia'}
            </button>
            <button
              className="vsj-btn-text vsj-btn-text--muted"
              onClick={() => goTo('result')}
            >
              Saltear y ver igualmente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 1 render ───────────────────────────────────────────────────────────

  return (
    <div className="vsj-step">
      <VsjStepHeader step={3} onBack={() => goTo('environment')} />

      <div className="vsj-property-form">
        <div>
          <h2 className="vsj-form-title">Contame sobre la propiedad</h2>
          <p className="vsj-form-intro">
            Solo tipo y superficie para ver la primera referencia.
          </p>
        </div>

        <div className="vsj-form-group">
          <label className="vsj-form-label">¿Qué tipo de propiedad es?</label>
          <div className="vsj-type-selector" role="radiogroup" aria-label="Tipo de propiedad">
            {TYPES.map(({ value, label, Icon }) => (
              <button
                key={value}
                role="radio"
                aria-checked={localType === value}
                className={
                  'vsj-type-card' + (localType === value ? ' vsj-type-card--selected' : '')
                }
                onClick={() => setLocalType(value)}
              >
                <span className="vsj-type-icon"><Icon size={24} /></span>
                <span className="vsj-type-label">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="vsj-form-group">
          <label className="vsj-form-label" htmlFor="vsj-surface-input">
            {localType === 'terreno'
              ? '¿Cuántos m² tiene el terreno?'
              : '¿Cuántos m² tiene la superficie cubierta?'}
          </label>
          <div className="vsj-surface-wrapper">
            <input
              id="vsj-surface-input"
              className="vsj-surface-input"
              type="number"
              inputMode="numeric"
              min="1"
              max="9999"
              placeholder="Ej: 80"
              value={localSurface}
              onChange={(e) => setLocalSurface(e.target.value)}
            />
            <span className="vsj-surface-unit">m²</span>
          </div>
          <p className="vsj-form-hint">
            Con un aproximado alcanza. No hace falta el dato exacto.
          </p>
        </div>

        <div className="vsj-property-form-footer">
          <button
            className="vsj-btn-primary"
            onClick={handleVerRef}
            disabled={!isStep1Valid || submitting}
            aria-disabled={!isStep1Valid || submitting}
          >
            {submitting ? 'Un momento…' : 'Ver la referencia'}
          </button>
          <button
            className="vsj-btn-outline"
            onClick={handleAfinar}
            disabled={!isStep1Valid || submitting}
            aria-disabled={!isStep1Valid || submitting}
          >
            Afinar datos primero
          </button>
          {!isStep1Valid && (
            <p className="vsj-form-footer-hint">
              Ingresá la superficie para continuar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
